import {gql} from '@apollo/client';
import {parseNumber} from '@benchling/js-utils';
import stringify from 'json-stable-stringify';
import {uniq} from 'underscore';

import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {LegacyMeasurement} from 'constants/MeasurementConstants';
import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {
  ExistingBioentityColumnDefinition,
  MeasurementAmountColumnDefinition,
  QuantityColumnDefinition,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {CustomEval, CustomEvalResult} from 'notebook/tables/constants/CustomEvalConstants';
import {ValueError, Warning} from 'notebook/tables/excess/Util';
import {
  CustomResolutionQueryType,
  InventoryTableQuantityAmountFromSourceContainerQuery,
  InventoryTableQuantityAmountFromSourcePlateQuery,
  InventoryTableQuantityUnitsFromSourceContainerQuery,
  InventoryTableQuantityUnitsFromSourcePlateQuery,
  InventoryTableValidateAddDeductFromSourceContainerQuery,
  InventoryTableValidateAddDeductFromSourcePlateQuery,
  ServerResolutionResult,
  TransferTableSourceQuantityFromSourceContainerQuery,
  TransferTableSourceQuantityFromSourcePlateQuery,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {resolveUnitsBySymbols} from 'notebook/tables/string-resolution/UnitsResolution';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {
  formatTypedValue,
  isTypedValueEmpty,
  toStringTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
  ResolveTypedValueToTypeFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {NumberTypedValue, StringTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {InvalidPositionError, parseGridPosition} from 'samples/SampleUtil';
import {PlateSchemaType} from 'samples/storables/StorableSchemaConstants';
import * as TransferUtil from 'samples/transfers/TransferUtil';
import {MinimalSourceContainerSummary} from 'samples/transfers/TransferUtil';
import lruMemoize from 'util/lruMemoize';
import {capitalize} from 'util/NameUtil';
import {Optional} from 'util/Types';

import {GetContainerQuantity, GetContainerQuantityVariables} from './__generated/GetContainerQuantity';
import {
  GetPlateWithWellQuantities,
  GetPlateWithWellQuantitiesVariables,
} from './__generated/GetPlateWithWellQuantities';
import {extractContainerId} from './extractContainerId';
import extractPlateId from './extractPlateId';

type WellOrInaccessible = NonNullable<
  ExtractByTypename<GetPlateWithWellQuantities['plates'][number], 'PlateNode'>['cells'][number][number]
>['container'];

export const GET_CONTAINER_QUANTITY_QUERY = gql`
  query GetContainerQuantity($containerIds: [StrictString]!) @skipAudit @engComponent(name: INVENTORY) {
    containers(ids: $containerIds) {
      ... on ContainerNode {
        id
        name
        quantityJson
        storableSchema {
          ... on StorableSchemaInterface {
            id
          }
        }
        ...HasArchiveRecordGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('HasArchiveRecord').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const GET_PLATE_WITH_WELL_QUANTITIES_QUERY = gql`
  query GetPlateWithWellQuantities($plateIds: [StrictString!]!) @skipAudit @engComponent(name: INVENTORY) {
    plates(ids: $plateIds) {
      ... on PlateNode {
        id
        name
        storableSchema {
          ... on PlateSchemaNode {
            id
            height
            width
            plateSchemaType
          }
        }
        cells {
          container {
            ... on ContainerNode {
              id
              name
              quantityJson
            }
            ...InaccessibleInfoGraphQL
          }
        }
        ...HasArchiveRecordGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('HasArchiveRecord').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

// To mock our GraphQL call, we need to wrap it in a method.
// This is different from most of our GraphQL tests, where we mock the Apollo cache.
// Here, mocking the cache won't work, because our fetch policy is `'no-cache'`.
export const stubbableFunctions = {
  async doContainerQuery(containerId: string): Promise<GetContainerQuantity> {
    return await doQueryWithoutCache<GetContainerQuantity, GetContainerQuantityVariables>(
      {query: GET_CONTAINER_QUANTITY_QUERY},
      {variables: {containerIds: [containerId]}}
    );
  },

  async doPlateQuery(plateId: string): Promise<GetPlateWithWellQuantities> {
    return await doQueryWithoutCache<GetPlateWithWellQuantities, GetPlateWithWellQuantitiesVariables>(
      {query: GET_PLATE_WITH_WELL_QUANTITIES_QUERY},
      {variables: {plateIds: [plateId]}}
    );
  },
};

export function getNumericCustomEvalResult({
  amountTypedValue,
  allowNegativeAmount,
}: {
  amountTypedValue: TypedValue;
  allowNegativeAmount: boolean;
}): {val: NumberTypedValue | StringTypedValue; error: ValueError | Warning | null; locked: false} {
  const formattedValue = formatTypedValue(amountTypedValue);
  const stringResolvedValue: StringTypedValue = {
    type: BaseTypedValueType.STRING,
    rawValue: formattedValue,
  };
  if (formattedValue === '') {
    return {
      val: stringResolvedValue,
      error: null,
      locked: false,
    };
  }
  const rawValue = parseNumber(formattedValue);
  const numberError = allowNegativeAmount
    ? null
    : new ValueError(`${formattedValue} is not a valid positive number.`);
  if (rawValue == null) {
    return {
      val: stringResolvedValue,
      error: numberError,
      locked: false,
    };
  }

  const resolvedValue: NumberTypedValue = {
    type: BaseTypedValueType.NUMBER,
    rawValue,
    defaultPrecision: null,
  };
  return {
    val: resolvedValue,
    error: rawValue < 0 ? numberError : null,
    locked: false,
  };
}

// TODO(BNCH-14955): dedupe with getCellWithResolvedNumberValue from the old transfer table?
export function getNonnegativeCustomEvalResult(quantityTypedValue: TypedValue): {
  val: NumberTypedValue | StringTypedValue;
  error: ValueError | Warning | null;
  locked: false;
} {
  return getNumericCustomEvalResult({amountTypedValue: quantityTypedValue, allowNegativeAmount: false});
}

/** The source container/well data the quantity custom eval validates the transfer quantity against. */
export interface SourceQuantityInfo {
  id: string;
  name: string;
  quantity: LegacyMeasurement;
}

function makeSourceQuantityResolutionResult(source: SourceQuantityInfo | null): ServerResolutionResult {
  return makeCustomResolutionResultFromTypedValue(
    source != null ? {type: BaseTypedValueType.JSON, jsonValue: source} : null
  );
}

/**
 * Resolvers for the source quantity a transfer quantity is validated against.
 *
 * These only fetch data; the comparison happens in the custom eval, which resolves the units it needs
 * through `CustomResolutionQueryType.UNITS`.
 */
export const TransferQuantityResolver = {
  resolveSourceQuantityFromSourceContainer: async (
    query: TransferTableSourceQuantityFromSourceContainerQuery
  ): Promise<ServerResolutionResult> => {
    const data = await stubbableFunctions.doContainerQuery(query.containerId);

    const container = data.containers[0];
    if (isInaccessible(container)) {
      return makeSourceQuantityResolutionResult(null);
    }

    const quantity: Optional<LegacyMeasurement> = container.quantityJson;
    if (quantity == null) {
      return makeSourceQuantityResolutionResult(null);
    }

    return makeSourceQuantityResolutionResult({id: container.id, name: container.name, quantity});
  },

  resolveSourceQuantityFromSourcePlate: async (
    query: TransferTableSourceQuantityFromSourcePlateQuery
  ): Promise<ServerResolutionResult> => {
    const data = await stubbableFunctions.doPlateQuery(query.plateId);

    const plate = data.plates[0];
    if (isInaccessible(plate)) {
      return makeSourceQuantityResolutionResult(null);
    }
    assert(plate.storableSchema.__typename === 'PlateSchemaNode', 'Expected a plate schema');

    let rowIndex: number;
    let columnIndex: number;
    try {
      ({rowIndex, columnIndex} = parseGridPosition({
        position: query.position,
        gridHeight: plate.storableSchema.height,
        gridWidth: plate.storableSchema.width,
      }));
    } catch (e) {
      if (e instanceof InvalidPositionError) {
        return makeSourceQuantityResolutionResult(null);
      } else {
        throw e;
      }
    }

    const inputWell = plate.cells[rowIndex][columnIndex]?.container;
    if (isInaccessible(inputWell)) {
      return makeSourceQuantityResolutionResult(null);
    }
    const quantity: Optional<LegacyMeasurement> = inputWell?.quantityJson;
    if (inputWell == null || quantity == null) {
      return makeSourceQuantityResolutionResult(null);
    }

    return makeSourceQuantityResolutionResult({id: inputWell.id, name: inputWell.name, quantity});
  },
};

/**
 * Compare a transfer quantity against the source's quantity, converting with the given units.
 *
 * Symbols missing from `getUnitBySymbol` make the comparison meaningless, so the transfer is not
 * flagged; see `TransferUtil.getQuantitiesForTransferValidation`. Callers that can tell the user which
 * unit is missing should do so before getting here, rather than relying on that fallback.
 */
export function getTransferQuantityError({
  sourceContainer,
  transferQuantityValue,
  transferQuantityUnits,
  getUnitBySymbol,
}: {
  sourceContainer: MinimalSourceContainerSummary;
  transferQuantityValue: number;
  transferQuantityUnits: string;
  getUnitBySymbol: TransferUtil.UnitBySymbolLookup;
}): ValueError | Warning | null {
  const {isValid, quantityErrorsAreBlocking} = TransferUtil.validateAllTransferQuantities(
    [
      {
        source: sourceContainer,
        transferQuantity: {value: transferQuantityValue, units: transferQuantityUnits},
      },
    ],
    getUnitBySymbol
  );
  if (isValid) {
    return null;
  }
  const message = TransferUtil.getTransferQuantityValidationText(sourceContainer, quantityErrorsAreBlocking);
  return quantityErrorsAreBlocking ? new ValueError(message) : new Warning(message);
}

/**
 * Validate a transfer quantity against its source, resolving both the source quantity and the units
 * needed to convert between them asynchronously.
 *
 * Each unresolved dependency surfaces as a `LoadingError`, which a later evaluation pass replaces with
 * the real result once the resolution completes.
 */
export function validateQuantityAgainstSource({
  quantityTypedValue,
  transferQuantityValue,
  transferQuantityUnits,
  sourceQuantityQuery,
  resolveFromResolutionQuery,
}: {
  quantityTypedValue: TypedValue;
  transferQuantityValue: number;
  transferQuantityUnits: string;
  sourceQuantityQuery:
    | TransferTableSourceQuantityFromSourceContainerQuery
    | TransferTableSourceQuantityFromSourcePlateQuery;
  resolveFromResolutionQuery: ResolveQueryFn;
}): CustomEvalResult {
  const {resolvedValue, error} = resolveFromResolutionQuery(
    quantityTypedValue,
    sourceQuantityQuery,
    makeTypedValueFromCustomResolvedValue
  );
  if (error != null) {
    return {val: quantityTypedValue, error, locked: false};
  }
  // Sources with nothing to validate against resolve to null, in which case the input typed value is
  // handed back to us.
  if (resolvedValue.type !== BaseTypedValueType.JSON) {
    return {val: quantityTypedValue, error: null, locked: false};
  }
  const sourceContainer: SourceQuantityInfo = resolvedValue.jsonValue;

  // A blank symbol means no unit was recorded, which is not the same as an unrecognized one: there is
  // nothing to convert with, so `getTransferQuantityError` skips the comparison rather than reporting it.
  const requiredUnitSymbols = uniq(
    [transferQuantityUnits, sourceContainer.quantity.units].filter(
      (symbol): symbol is string => symbol != null && symbol !== ''
    )
  );
  const {unitsBySymbol, error: unitsError} = resolveUnitsBySymbols(
    requiredUnitSymbols,
    resolveFromResolutionQuery
  );
  if (unitsError != null) {
    return {val: quantityTypedValue, error: unitsError, locked: false};
  }
  // A symbol the backend does not recognize is stale stored data. Say so, rather than skipping the
  // comparison and leaving the cell looking validated.
  const missingUnitSymbols = requiredUnitSymbols.filter((symbol) => unitsBySymbol![symbol] == null);
  if (missingUnitSymbols.length > 0) {
    return {
      val: quantityTypedValue,
      error: new ValueError(missingUnitSymbols.map((symbol) => `${symbol} is not a valid unit.`).join(' ')),
      locked: false,
    };
  }

  return {
    val: quantityTypedValue,
    error: getTransferQuantityError({
      sourceContainer,
      transferQuantityValue,
      transferQuantityUnits,
      getUnitBySymbol: (symbol) => unitsBySymbol![symbol] ?? null,
    }),
    locked: false,
  };
}

/** Parsing a quantity cell drops its precision, so keep the cell's own value when it already had one. */
function keepExistingPrecision(quantityTypedValue: TypedValue, parsedValue: NumberTypedValue): TypedValue {
  return quantityTypedValue.type === BaseTypedValueType.NUMBER ? quantityTypedValue : parsedValue;
}

export const TransferQuantityCustomEval = {
  quantityWithSourceEntityCustomEval: (
    quantityTypedValue: TypedValue,
    columnDefinition: QuantityColumnDefinition
  ): CustomEvalResult => getNonnegativeCustomEvalResult(quantityTypedValue),

  quantityWithSourceContainerCustomEval: (
    quantityTypedValue: TypedValue,
    columnDefinition: QuantityColumnDefinition,
    [containerTypedValue]: [TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ): CustomEvalResult => {
    const numericResult = getNonnegativeCustomEvalResult(quantityTypedValue);
    if (numericResult.val.type !== BaseTypedValueType.NUMBER || numericResult.error) {
      return numericResult;
    }

    const containerId = extractContainerId(containerTypedValue);
    if (containerId == null) {
      return {val: quantityTypedValue, error: null, locked: false};
    }

    return validateQuantityAgainstSource({
      quantityTypedValue: keepExistingPrecision(quantityTypedValue, numericResult.val),
      transferQuantityValue: numericResult.val.rawValue,
      transferQuantityUnits: columnDefinition.units,
      sourceQuantityQuery: {
        customResolutionQueryType:
          CustomResolutionQueryType.TRANSFER_TABLE_SOURCE_QUANTITY_FROM_SOURCE_CONTAINER,
        containerId,
        type: ResolutionQueryType.CUSTOM,
      },
      resolveFromResolutionQuery,
    });
  },

  quantityWithSourcePlateCustomEval: (
    quantityTypedValue: TypedValue,
    columnDefinition: QuantityColumnDefinition,
    [plateTypedValue, positionTypedValue]: [TypedValue | null, TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ): CustomEvalResult => {
    const numericResult = getNonnegativeCustomEvalResult(quantityTypedValue);
    if (numericResult.val.type !== BaseTypedValueType.NUMBER || numericResult.error) {
      return numericResult;
    }

    if (!positionTypedValue || isTypedValueEmpty(positionTypedValue)) {
      return {val: quantityTypedValue, error: null, locked: false};
    }

    const position = formatTypedValue(positionTypedValue);
    const plateId = extractPlateId(plateTypedValue);
    if (plateId == null) {
      return {val: quantityTypedValue, error: null, locked: false};
    }

    return validateQuantityAgainstSource({
      quantityTypedValue: keepExistingPrecision(quantityTypedValue, numericResult.val),
      transferQuantityValue: numericResult.val.rawValue,
      transferQuantityUnits: columnDefinition.units,
      sourceQuantityQuery: {
        customResolutionQueryType: CustomResolutionQueryType.TRANSFER_TABLE_SOURCE_QUANTITY_FROM_SOURCE_PLATE,
        plateId,
        position,
        type: ResolutionQueryType.CUSTOM,
      },
      resolveFromResolutionQuery,
    });
  },
};

export function validateAddDeductAgainstQuantity({
  inputQuantity,
  addDeductValue,
  isPlateWell,
}: {
  inputQuantity: LegacyMeasurement;
  addDeductValue: number;
  isPlateWell: boolean;
}): ServerResolutionResult {
  const storableLabel = isPlateWell ? 'well' : 'container';
  if (inputQuantity.units == null || inputQuantity.value == null) {
    return {
      type: undefined,
      error: `Cannot add or deduct from a ${storableLabel} with an unspecified quantity.`,
      resolvedValue: undefined,
    };
  }
  if (inputQuantity.value === 0 && addDeductValue < 0) {
    return {
      type: undefined,
      error: `Cannot deduct from an empty ${storableLabel}.`,
      resolvedValue: undefined,
    };
  }
  if (inputQuantity.value + addDeductValue < 0) {
    return {
      type: undefined,
      error: `A larger quantity is being deducted than exists in the ${storableLabel}. ${capitalize(
        storableLabel
      )} quantity will be set to 0.`,
      isWarning: true,
      resolvedValue: undefined,
    };
  }
  return makeCustomResolutionResultFromTypedValue(null);
}

function getQuantityFromWell(well?: WellOrInaccessible): Optional<LegacyMeasurement> {
  if (isInaccessible(well)) {
    return null;
  }
  return well?.quantityJson;
}

export const InventoryQuantityResolver = {
  autopopulateContainerQuantityAmount: async (
    query: InventoryTableQuantityAmountFromSourceContainerQuery
  ): Promise<ServerResolutionResult> => {
    const data = await stubbableFunctions.doContainerQuery(query.containerId);
    const container = data.containers[0];
    if (isInaccessible(container)) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    if (query.containerSchemaId != null && query.containerSchemaId !== container.storableSchema.id) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    const quantity: Optional<LegacyMeasurement> = container.quantityJson;
    if (quantity == null || quantity.value == null) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    const typedValue = toStringTypedValue(quantity.value.toString());
    return makeCustomResolutionResultFromTypedValue(typedValue);
  },

  autopopulateContainerQuantityUnits: async (
    query: InventoryTableQuantityUnitsFromSourceContainerQuery
  ): Promise<ServerResolutionResult> => {
    const data = await stubbableFunctions.doContainerQuery(query.containerId);
    const container = data.containers[0];
    if (isInaccessible(container)) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    if (query.containerSchemaId != null && query.containerSchemaId !== container.storableSchema.id) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    const quantity: Optional<LegacyMeasurement> = container.quantityJson;
    if (quantity == null || quantity.units == null) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    return makeCustomResolutionResultFromTypedValue(toStringTypedValue(quantity.units));
  },

  autopopulatePlateQuantityAmount: async (
    query: InventoryTableQuantityAmountFromSourcePlateQuery
  ): Promise<ServerResolutionResult> => {
    const data = await stubbableFunctions.doPlateQuery(query.plateId);

    const plate = data.plates[0];
    if (isInaccessible(plate)) {
      return makeCustomResolutionResultFromTypedValue(null);
    }
    assert(plate.storableSchema.__typename === 'PlateSchemaNode', 'Expected a plate schema');

    if (query.plateSchemaId != null && query.plateSchemaId !== plate.storableSchema.id) {
      return makeCustomResolutionResultFromTypedValue(null);
    }
    if (plate.storableSchema.plateSchemaType !== PlateSchemaType.FIXED_PLATE) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    let rowIndex: number;
    let columnIndex: number;
    try {
      ({rowIndex, columnIndex} = parseGridPosition({
        position: query.position,
        gridHeight: plate.storableSchema.height,
        gridWidth: plate.storableSchema.width,
      }));
    } catch (e) {
      if (e instanceof InvalidPositionError) {
        return makeCustomResolutionResultFromTypedValue(null);
      } else {
        throw e;
      }
    }

    const quantity = getQuantityFromWell(plate.cells[rowIndex][columnIndex]?.container);
    if (quantity == null || quantity.value == null) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    const typedValue = toStringTypedValue(quantity.value.toString());
    return makeCustomResolutionResultFromTypedValue(typedValue);
  },

  autopopulatePlateQuantityUnits: async (
    query: InventoryTableQuantityUnitsFromSourcePlateQuery
  ): Promise<ServerResolutionResult> => {
    const data = await stubbableFunctions.doPlateQuery(query.plateId);

    const plate = data.plates[0];
    if (isInaccessible(plate)) {
      return makeCustomResolutionResultFromTypedValue(null);
    }
    assert(plate.storableSchema.__typename === 'PlateSchemaNode', 'Expected a plate schema');

    if (query.plateSchemaId != null && query.plateSchemaId !== plate.storableSchema.id) {
      return makeCustomResolutionResultFromTypedValue(null);
    }
    if (plate.storableSchema.plateSchemaType !== PlateSchemaType.FIXED_PLATE) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    let rowIndex: number;
    let columnIndex: number;
    try {
      ({rowIndex, columnIndex} = parseGridPosition({
        position: query.position,
        gridHeight: plate.storableSchema.height,
        gridWidth: plate.storableSchema.width,
      }));
    } catch (e) {
      if (e instanceof InvalidPositionError) {
        return makeCustomResolutionResultFromTypedValue(null);
      } else {
        throw e;
      }
    }

    const quantity: Optional<LegacyMeasurement> = getQuantityFromWell(
      plate.cells[rowIndex][columnIndex]?.container
    );
    if (quantity == null || quantity.units == null) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    return makeCustomResolutionResultFromTypedValue(toStringTypedValue(quantity.units));
  },

  validateAddDeductFromSourceContainer: async (
    query: InventoryTableValidateAddDeductFromSourceContainerQuery
  ): Promise<ServerResolutionResult> => {
    const data = await stubbableFunctions.doContainerQuery(query.containerId);
    const container = data.containers[0];
    if (isInaccessible(container)) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    const quantity: Optional<LegacyMeasurement> = container.quantityJson;
    if (quantity == null) {
      return makeCustomResolutionResultFromTypedValue(null);
    }
    if (isArchived(container)) {
      return {
        type: undefined,
        error: `Cannot add or deduct from an archived container.`,
        resolvedValue: undefined,
      };
    }
    return validateAddDeductAgainstQuantity({
      inputQuantity: quantity,
      addDeductValue: query.addDeductValue,
      isPlateWell: false,
    });
  },

  validateAddDeductFromSourcePlate: async (
    query: InventoryTableValidateAddDeductFromSourcePlateQuery
  ): Promise<ServerResolutionResult> => {
    const data = await stubbableFunctions.doPlateQuery(query.plateId);

    const plate = data.plates[0];
    if (isInaccessible(plate)) {
      return makeCustomResolutionResultFromTypedValue(null);
    }
    assert(plate.storableSchema.__typename === 'PlateSchemaNode', 'Expected a plate schema');

    let rowIndex: number;
    let columnIndex: number;
    try {
      ({rowIndex, columnIndex} = parseGridPosition({
        position: query.position,
        gridHeight: plate.storableSchema.height,
        gridWidth: plate.storableSchema.width,
      }));
    } catch (e) {
      if (e instanceof InvalidPositionError) {
        return makeCustomResolutionResultFromTypedValue(null);
      } else {
        throw e;
      }
    }

    const quantity: Optional<LegacyMeasurement> = getQuantityFromWell(
      plate.cells[rowIndex][columnIndex]?.container
    );

    if (quantity == null) {
      return makeCustomResolutionResultFromTypedValue(null);
    }
    if (isArchived(plate)) {
      return {
        type: undefined,
        error: `Cannot add or deduct from wells in an archived plate.`,
        resolvedValue: undefined,
      };
    }

    return validateAddDeductAgainstQuantity({
      inputQuantity: quantity,
      addDeductValue: query.addDeductValue,
      isPlateWell: true,
    });
  },
};

function _getQuantityAmountFromSourceContainerCustomEval<CustomEvalOptions>({
  containerSchemaId,
}: {containerSchemaId?: string | null} = {}): CustomEval<CustomEvalOptions> {
  return (
    quantityTypedValue: TypedValue,
    columnDefinition: ExistingBioentityColumnDefinition,
    [containerTypedValue]: [TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ) => {
    if (!isTypedValueEmpty(quantityTypedValue)) {
      return {val: quantityTypedValue, error: null, locked: false};
    }

    const containerId = extractContainerId(containerTypedValue);
    if (containerId == null) {
      return {val: quantityTypedValue, error: null, locked: false};
    }

    const {resolvedValue, error} = resolveFromResolutionQuery(
      quantityTypedValue,
      {
        customResolutionQueryType:
          CustomResolutionQueryType.INVENTORY_TABLE_QUANTITY_AMOUNT_FROM_SOURCE_CONTAINER,
        containerId,
        ...(containerSchemaId != null && {containerSchemaId}),
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {val: resolvedValue, error, locked: false};
  };
}

function _getQuantityUnitsFromSourceContainerCustomEval<CustomEvalOptions>({
  containerSchemaId,
}: {containerSchemaId?: string | null} = {}): CustomEval<CustomEvalOptions> {
  return (
    quantityTypedValue: TypedValue,
    columnDefinition: ExistingBioentityColumnDefinition,
    [containerTypedValue]: [TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ) => {
    if (!isTypedValueEmpty(quantityTypedValue)) {
      return {val: quantityTypedValue, error: null, locked: false};
    }

    const containerId = extractContainerId(containerTypedValue);
    if (containerId == null) {
      return {val: quantityTypedValue, error: null, locked: false};
    }

    const {resolvedValue, error} = resolveFromResolutionQuery(
      quantityTypedValue,
      {
        customResolutionQueryType:
          CustomResolutionQueryType.INVENTORY_TABLE_QUANTITY_UNITS_FROM_SOURCE_CONTAINER,
        containerId,
        ...(containerSchemaId != null && {containerSchemaId}),
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {val: resolvedValue, error, locked: false};
  };
}

function _getQuantityAmountFromSourcePlateCustomEval<CustomEvalOptions>({
  plateSchemaId,
}: {plateSchemaId?: string | null} = {}): CustomEval<CustomEvalOptions> {
  return (
    quantityTypedValue: TypedValue,
    columnDefinition: QuantityColumnDefinition,
    [plateTypedValue, positionTypedValue]: [TypedValue | null, TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ) => {
    if (!positionTypedValue || isTypedValueEmpty(positionTypedValue)) {
      return {val: quantityTypedValue, error: null, locked: false};
    }

    const position = formatTypedValue(positionTypedValue);
    const plateId = extractPlateId(plateTypedValue);
    if (plateId == null) {
      return {val: quantityTypedValue, error: null, locked: false};
    }

    const {resolvedValue, error} = resolveFromResolutionQuery(
      quantityTypedValue,
      {
        customResolutionQueryType:
          CustomResolutionQueryType.INVENTORY_TABLE_QUANTITY_AMOUNT_FROM_SOURCE_PLATE,
        plateId,
        position,
        ...(plateSchemaId != null && {plateSchemaId}),
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {val: resolvedValue, error, locked: false};
  };
}

function _getQuantityUnitsFromSourcePlateCustomEval<CustomEvalOptions>({
  plateSchemaId,
}: {plateSchemaId?: string | null} = {}): CustomEval<CustomEvalOptions> {
  return (
    quantityTypedValue: TypedValue,
    columnDefinition: QuantityColumnDefinition,
    [plateTypedValue, positionTypedValue]: [TypedValue | null, TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ) => {
    if (!positionTypedValue || isTypedValueEmpty(positionTypedValue)) {
      return {val: quantityTypedValue, error: null, locked: false};
    }

    const position = formatTypedValue(positionTypedValue);
    const plateId = extractPlateId(plateTypedValue);
    if (plateId == null) {
      return {val: quantityTypedValue, error: null, locked: false};
    }

    const {resolvedValue, error} = resolveFromResolutionQuery(
      quantityTypedValue,
      {
        customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_QUANTITY_UNITS_FROM_SOURCE_PLATE,
        plateId,
        position,
        ...(plateSchemaId != null && {plateSchemaId}),
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {val: resolvedValue, error, locked: false};
  };
}

export const InventoryQuantityCustomEval = {
  getQuantityAmountFromSourceContainerCustomEval: lruMemoize(
    _getQuantityAmountFromSourceContainerCustomEval,
    {max: 100},
    (...args) => stringify(args)
  ),

  getQuantityAmountFromSourcePlateCustomEval: lruMemoize(
    _getQuantityAmountFromSourcePlateCustomEval,
    {max: 100},
    (...args) => stringify(args)
  ),

  getQuantityUnitsFromSourceContainerCustomEval: lruMemoize(
    _getQuantityUnitsFromSourceContainerCustomEval,
    {max: 100},
    (...args) => stringify(args)
  ),

  getQuantityUnitsFromSourcePlateCustomEval: lruMemoize(
    _getQuantityUnitsFromSourcePlateCustomEval,
    {max: 100},
    (...args) => stringify(args)
  ),

  addDeductFromSourceContainerCustomEval: (
    typedValue: TypedValue,
    columnDefinition: MeasurementAmountColumnDefinition,
    [containerTypedValue]: [TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn,
    resolveTypedValueToType: ResolveTypedValueToTypeFn
  ): CustomEvalResult => {
    const numericResult = getNumericCustomEvalResult({
      amountTypedValue: typedValue,
      allowNegativeAmount: true,
    });
    if (numericResult.val.type !== BaseTypedValueType.NUMBER || numericResult.error) {
      return numericResult;
    }
    const containerId = extractContainerId(containerTypedValue);
    if (containerId == null) {
      return {val: typedValue, error: null, locked: false};
    }

    const {resolvedValue: customResolvedValue, error} = resolveFromResolutionQuery(
      typedValue,
      {
        customResolutionQueryType:
          CustomResolutionQueryType.INVENTORY_TABLE_VALIDATE_ADD_DEDUCT_FROM_SOURCE_CONTAINER,
        type: ResolutionQueryType.CUSTOM,
        containerId,
        addDeductValue: numericResult.val.rawValue,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {val: customResolvedValue, error, locked: false};
  },

  addDeductFromSourcePlateCustomEval: (
    typedValue: TypedValue,
    columnDefinition: MeasurementAmountColumnDefinition,
    [plateTypedValue, positionTypedValue]: [TypedValue | null, TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn,
    resolveTypedValueToType: ResolveTypedValueToTypeFn
  ): CustomEvalResult => {
    const numericResult = getNumericCustomEvalResult({
      amountTypedValue: typedValue,
      allowNegativeAmount: true,
    });
    if (numericResult.val.type !== BaseTypedValueType.NUMBER || numericResult.error) {
      return numericResult;
    }
    if (!positionTypedValue || isTypedValueEmpty(positionTypedValue)) {
      return {val: typedValue, error: null, locked: false};
    }

    const position = formatTypedValue(positionTypedValue);
    const plateId = extractPlateId(plateTypedValue);
    if (plateId == null) {
      return {val: typedValue, error: null, locked: false};
    }

    const {resolvedValue: customResolvedValue, error} = resolveFromResolutionQuery(
      typedValue,
      {
        customResolutionQueryType:
          CustomResolutionQueryType.INVENTORY_TABLE_VALIDATE_ADD_DEDUCT_FROM_SOURCE_PLATE,
        type: ResolutionQueryType.CUSTOM,
        plateId,
        position,
        addDeductValue: numericResult.val.rawValue,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {val: customResolvedValue, error, locked: false};
  },
};
